import { Badge } from '@/components/ui/badge'
import type { FormPreviewStep } from '@/contracts/form-preview'
import { CheckCircle2, FileText, Flag, Layers3, PlayCircle } from 'lucide-react'
import { useEffect, useRef } from 'react'

function stepIcon(kind: FormPreviewStep['kind']) {
  if (kind === 'welcome') return <PlayCircle className="size-4" />
  if (kind === 'group') return <Layers3 className="size-4" />
  if (kind === 'completion') return <Flag className="size-4" />
  return <FileText className="size-4" />
}

export function FormPreviewStepNavigator({
  steps,
  activeStepId,
}: {
  steps: FormPreviewStep[]
  activeStepId: string
}) {
  const itemRefs = useRef(new Map<string, HTMLDivElement>())

  useEffect(() => {
    const activeItem = itemRefs.current.get(activeStepId)
    if (!activeItem) return

    activeItem.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' })

    const activeElement = document.activeElement
    const shouldMoveKeyboardFocus =
      !activeElement ||
      activeElement === document.body ||
      activeElement === document.documentElement ||
      activeItem.parentElement?.contains(activeElement)

    if (shouldMoveKeyboardFocus) activeItem.focus({ preventScroll: true })
  }, [activeStepId])

  return (
    <aside
      className="min-h-0 overflow-x-hidden overflow-y-auto border-r border-slate-200 bg-white [scrollbar-color:#cbd5e1_transparent] [scrollbar-gutter:stable] [scrollbar-width:thin] [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-slate-300 [&::-webkit-scrollbar-thumb:hover]:bg-slate-400 [&::-webkit-scrollbar-track]:bg-transparent"
      data-testid="form-preview-step-navigator"
    >
      <div className="sticky top-0 z-10 border-b border-slate-200 bg-white px-4 py-3">
        <div className="text-sm font-semibold text-slate-950">預覽流程</div>
        <div className="mt-1 text-xs text-slate-500">{steps.length} 個步驟</div>
      </div>
      <div className="space-y-1 p-3" role="list" aria-label="預覽步驟">
        {steps.map((step, index) => {
          const active = step.id === activeStepId
          return (
            <div
              key={step.id}
              ref={(node) => {
                if (node) itemRefs.current.set(step.id, node)
                else itemRefs.current.delete(step.id)
              }}
              role="listitem"
              aria-label={`${index + 1}. ${step.label}`}
              tabIndex={active ? 0 : -1}
              aria-current={active ? 'step' : undefined}
              data-active={active ? 'true' : undefined}
              className={`flex scroll-m-3 items-start gap-3 rounded-md border px-3 py-2 text-left outline-none transition-[background-color,border-color,box-shadow,color] focus-visible:ring-2 focus-visible:ring-[#4a2f21]/35 ${
                active
                  ? 'border-[#4a2f21]/30 bg-[#4a2f21]/10 text-[#4a2f21] shadow-sm'
                  : 'border-transparent text-slate-600 hover:bg-slate-50'
              }`}
            >
              <div className={active ? 'mt-0.5 text-[#4a2f21]' : 'mt-0.5 text-slate-600'}>
                {stepIcon(step.kind)}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2">
                  <span
                    className={
                      active
                        ? 'text-xs font-medium text-[#4a2f21]'
                        : 'text-xs font-medium text-slate-600'
                    }
                  >
                    {index + 1}
                  </span>
                  <span className="truncate text-sm font-medium">{step.label}</span>
                </div>
                {step.kind === 'group' && (
                  <div className="mt-1 flex flex-wrap gap-1">
                    <Badge variant="outline">{step.questionIds.length} 題</Badge>
                    {step.mode && (
                      <Badge variant={step.mode === 'synced' ? 'warning' : 'secondary'}>
                        {step.mode === 'synced' ? '同步題組' : '複本題組'}
                      </Badge>
                    )}
                  </div>
                )}
                {step.kind === 'question' && !step.answerable && (
                  <div className="mt-1 text-xs text-slate-600">版面區塊</div>
                )}
              </div>
              {active && <CheckCircle2 className="mt-0.5 size-4 shrink-0 text-[#4a2f21]" />}
            </div>
          )
        })}
      </div>
    </aside>
  )
}
